// История версий протокола (029: contracts/api.md, «Схемы»; research Р1–Р5; VER-02, VER-03,
// VER-04, VER-05, VER-06, VER-07). Версии не хранятся: список — группировка ленты «Изменения»
// текущего поколения, состояние версии — обратный проход по истории пунктов. Хранятся только
// имена версий (`minutes.version_names`, только на добавление).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, PersonRefSchema, TimestampSchema } from '../kernel/common.ts';
import { withoutControlChars } from './item.ts';
import { SnapshotBodySchema } from './snapshot.ts';

/** Версий на странице списка. */
export const VERSIONS_PAGE = 50;
/** Длина имени версии, как у названия снимка недели. */
export const VERSION_NAME_MAX = 120;
/** Пауза, после которой правки того же автора — новая версия (FR-004). */
export const VERSION_GAP_MINUTES = 30;
/** Пределы «Вернуть выделенное»: ячеек и целых строк в одной команде. */
export const VERSION_REVERT_CELLS_MAX = 5000;
export const VERSION_REVERT_ROWS_MAX = 2000;

/**
 * Путь версии: протокол и ревизия конца версии. Имя не VersionParamsSchema из contracts/api.md:
 * оно занято версией файла документа (api/documents.ts).
 */
export const ProtocolVersionParamsSchema = z.object({
  id: IdSchema,
  revision: z.coerce.number().int().min(1),
});

/** Имя версии: 1–120 знаков, одна строка без управляющих знаков — как название снимка. */
export const VersionNameSchema = z
  .string()
  .max(VERSION_NAME_MAX)
  .refine((name) => name.trim().length > 0, { message: 'Введите имя' })
  .refine((name) => withoutControlChars(name) === name && !/[\n\r\t]/.test(name), {
    message: 'В имени — управляющий знак или перевод строки',
  });

/** Вид версии: правки; восстановление; перевод или создание; фиксация недели. */
export const versionKinds = ['edits', 'restore', 'origin', 'week'] as const;
export const VersionKindSchema = z.enum(versionKinds);
export type VersionKind = z.infer<typeof VersionKindSchema>;

export const VersionEntrySchema = z.object({
  kind: VersionKindSchema,
  /** Ревизия начала и конца: состояние версии — на конце. */
  startRevision: z.number().int().min(1),
  revision: z.number().int().min(1),
  startAt: TimestampSchema,
  endAt: TimestampSchema,
  actor: PersonRefSchema,
  /** Правок (строк ленты) в версии. */
  edits: z.number().int().min(1),
  name: z.object({ text: z.string(), by: PersonRefSchema, at: TimestampSchema }).nullable(),
  /** kind = week: снимок недели (открывается как снимок 016). */
  snapshot: z.object({ id: IdSchema, weekDate: DateSchema, title: z.string() }).nullable(),
  /** kind = restore: какая версия восстановлена. */
  restoredFrom: z.object({ revision: z.number().int().min(1), at: TimestampSchema }).nullable(),
});
export type VersionEntry = z.infer<typeof VersionEntrySchema>;

export const VersionsQuerySchema = z.strictObject({
  /** Курсор: версии, начатые раньше этой ревизии. */
  before: z.coerce.number().int().min(2).optional(),
  /** «Только с именем»: версии с именем и снимки недель. */
  named: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});
export type VersionsQuery = z.infer<typeof VersionsQuerySchema>;

export const VersionListSchema = z.object({
  /** Новые сверху; без курсора первая — текущая версия (её конец — ревизия протокола). */
  items: z.array(VersionEntrySchema).max(VERSIONS_PAGE),
  next: z.number().int().min(2).nullable(),
  /** Ревизия протокола при чтении. */
  revision: z.number().int().min(1),
});
export type VersionList = z.infer<typeof VersionListSchema>;

/** Изменение версии для подсветки: колонка — ключ ProtocolColumn; null — строка целиком. */
export const VersionChangeSchema = z.object({
  itemId: IdSchema,
  column: z.string().nullable(),
  actor: PersonRefSchema,
  at: TimestampSchema,
});
export type VersionChange = z.infer<typeof VersionChangeSchema>;

export const VersionViewQuerySchema = z.strictObject({
  /** Подсветка — правки с ревизии from по ревизию версии; по умолчанию — только сама ревизия. */
  from: z.coerce.number().int().min(1).optional(),
});
export type VersionViewQuery = z.infer<typeof VersionViewQuerySchema>;

export const VersionViewSchema = z.object({
  revision: z.number().int().min(1),
  from: z.number().int().min(1),
  /** Время правки revision. */
  at: TimestampSchema,
  /** Автор правки revision. */
  actor: PersonRefSchema,
  /** Имя версии с концом revision. */
  name: z.string().nullable(),
  /** Тело в форме снимка: колонки — нынешние, пункты — на ревизию (research Р1, Р4, Р5). */
  body: SnapshotBodySchema,
  changes: z.array(VersionChangeSchema),
  /** Пункты, действующие в версии и архивные сейчас — «Сейчас в архиве» (FR-015). */
  archivedNow: z.array(IdSchema),
});
export type VersionView = z.infer<typeof VersionViewSchema>;

/** Числа окна «Восстановить эту версию» — на текущем состоянии (FR-018). */
export const RestorePreviewSchema = z.object({
  /** Ревизия протокола, на которой посчитаны числа: база команды. */
  baseRevision: z.number().int().min(1),
  /** «Изменится пунктов: N» — действующих после восстановления. */
  items: z.number().int().min(0),
  /** «Вернутся из архива: M строк». */
  restored: z.number().int().min(0),
  /** «Уйдут в архив: K строк (созданы позже)». */
  archived: z.number().int().min(0),
  /** «Будут отменены правки X человек» — авторы после версии, кроме себя. */
  people: z.number().int().min(0),
  /** Недели, зафиксированные после версии. */
  weeks: z.array(DateSchema),
  /** «Протокол уже такой». */
  same: z.boolean(),
});
export type RestorePreview = z.infer<typeof RestorePreviewSchema>;

/** Ячейка выделения: пункт и ключ колонки протокола. */
export const VersionCellRefSchema = z.strictObject({
  itemId: IdSchema,
  column: z.string().min(1).max(40),
});
export type VersionCellRef = z.infer<typeof VersionCellRefSchema>;

export const VersionSelectionSchema = z
  .strictObject({
    cells: z.array(VersionCellRefSchema).max(VERSION_REVERT_CELLS_MAX),
    rows: z.array(IdSchema).max(VERSION_REVERT_ROWS_MAX),
  })
  .refine((selection) => selection.cells.length + selection.rows.length > 0, {
    message: 'Выделите ячейки или строки',
  });
export type VersionSelection = z.infer<typeof VersionSelectionSchema>;

/** Числа окна «Вернуть выделенное». */
export const RevertPreviewSchema = z.object({
  baseRevision: z.number().int().min(1),
  /** Пунктов изменится. */
  items: z.number().int().min(0),
  /** Строк вернётся из архива (с предками). */
  rows: z.number().int().min(0),
  /** Из них — архивных предков, не выделенных. */
  parents: z.number().int().min(0),
  /** «Выделенное уже такое». */
  same: z.boolean(),
});
export type RevertPreview = z.infer<typeof RevertPreviewSchema>;

/**
 * Имя версии и имя, которое видел человек, открывая поле (сходимость, F3; принцип XII): другое
 * текущее имя — REC-001, чужое переименование не затирается молча. Нет `base` — ответ до 029-F3.
 */
export const VersionNameBodySchema = z.strictObject({
  name: VersionNameSchema,
  base: z.string().max(VERSION_NAME_MAX).nullable().optional(),
});
export type VersionNameBody = z.infer<typeof VersionNameBodySchema>;

export const VersionNameResponseSchema = VersionEntrySchema.shape.name.unwrap();
export type VersionNameResponse = z.infer<typeof VersionNameResponseSchema>;
